import styles from "./GymNotificationsUserPopup.module.scss";
import React, { useMemo, useState } from "react";
import closeIcon from "../../../assets/images/icons/close-grey.svg";
import { useGetGymNotificationByGymListingIdQuery } from "../../../api/queries";
import { Table, TableProps } from "antd";
import { IUserGymNotificationWithUserInfo } from "../../../models/GymNotification";
import { ColumnsType } from "antd/es/table";
import dayjs from "dayjs";
import dots from "../../../assets/images/icons/dots.svg";
import editIcon from "../../../assets/images/icons/edit-dark.svg";
import { useTranslation } from "react-i18next";
import basketIcon from "../../../assets/images/icons/basket-black.svg";
import { useUserGymNotificationMutations } from "../../../api/mutations";
import { handleError } from "../../../helper/errorHandler";
import useAppDispatch from "../../../hooks/useAppDispatch";
import { openSuccessMessage } from "../../../store/message/messageSlice";

interface IGymNotificationsUserPopupProps {
  gymListingId: string;
  getData(): void;
  closePopup(): void;
}

const GymNotificationsUserPopup: React.FC<IGymNotificationsUserPopupProps> = ({
  gymListingId,
  getData,
  closePopup,
}) => {
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const [tableParams, setTableParams] = useState<{ currentPage: number; pageSize: number }>({
    currentPage: 1,
    pageSize: 10,
  });
  const {
    data = { gymName: "", gymLocation: "", items: [], total: 0 },
    isLoading,
    refetch,
  } = useGetGymNotificationByGymListingIdQuery({ ...tableParams, gymListingId });
  const { deleteById, updateSentDate } = useUserGymNotificationMutations();

  const totalCount = useMemo(() => {
    if (!data) return 0;
    return data.total;
  }, [data]);

  const handleTableChange: TableProps<IUserGymNotificationWithUserInfo>["onChange"] = (
    pagination
  ) => {
    setTableParams({
      currentPage: pagination.current || 1,
      pageSize: pagination.pageSize || 10,
    });
  };

  const remove = async (id: string) => {
    deleteById(id, {
      onSuccess: () => {
        refetch();
        getData();
        dispatch(openSuccessMessage(t("GymNotificationRemovedSuccessfully")));
      },
      onError: (e) => handleError(e, dispatch, t),
    });
  };

  const updateSentDateHandler = (id: string) => {
    updateSentDate(id, {
      onSuccess: () => {
        refetch();
        dispatch(openSuccessMessage(t("SentDateChangedSuccessfully")));
      },
      onError: (e) => handleError(e, dispatch, t),
    });
  };

  const columns: ColumnsType<IUserGymNotificationWithUserInfo> = [
    {
      title: t("UserName"),
      render: (data: IUserGymNotificationWithUserInfo) => {
        return `${data.user.firstname} ${data.user.lastname}`;
      },
    },
    {
      title: t("Email"),
      render: (data: IUserGymNotificationWithUserInfo) => {
        return `${data.user.email}`;
      },
    },
    {
      title: t("DataSubscribed"),
      render: (data: IUserGymNotificationWithUserInfo) => {
        return dayjs(data.createdAt).format("MMM D, YYYY");
      },
    },
    {
      title: t("NotificationSent"),
      render: (data: IUserGymNotificationWithUserInfo) => {
        if (data.dateOfSent) {
          return (
            <span style={{ color: "#32BC69" }}>
              {t("Yes")}({dayjs(data.dateOfSent).format("MMM D, YYYY")})
            </span>
          );
        } else {
          return <span style={{ color: "#D93131" }}>{t("No")}</span>;
        }
      },
    },
    {
      title: t("Action"),
      render: (data: IUserGymNotificationWithUserInfo) => {
        return (
          <div className={"action-wrapper"}>
            <img src={dots} alt={"action"} />
            <ul className={"dropdown"}>
              <li className={"dropdown-item"} onClick={() => updateSentDateHandler(data.id)}>
                <img src={editIcon} alt={"edit"} />
                {data.dateOfSent ? t("MarkAsUnSend") : t("MarkAsSent")}
              </li>
              <li className={"dropdown-item"} onClick={() => remove(data.id)}>
                <img style={{ width: "20px" }} src={basketIcon} alt={"delete"} />
                {t("Remove")}
              </li>
            </ul>
          </div>
        );
      },
    },
  ];

  return (
    <div className={styles.container}>
      <div className={styles.popup}>
        <button className={styles.close} onClick={closePopup}>
          <img src={closeIcon} alt={"close"} />
        </button>

        <h2 className={styles.title}>{data.gymName}</h2>
        <h3 className={styles.subtitle}>{data.gymLocation}</h3>

        <div className={`${styles.scrollBox} custom-scroll`}>
          <Table
            className={"table"}
            columns={columns}
            rowKey={(record) => record.id}
            dataSource={data.items}
            pagination={{ ...tableParams, total: totalCount }}
            loading={isLoading}
            onChange={handleTableChange}
          />
        </div>
      </div>
    </div>
  );
};

export default GymNotificationsUserPopup;
